
import { App, State } from '../app/index.js';
import { getBadge, UNAVAILABLE } from '../data/storage.js';
import { UI } from '../ui/status.js';
import { renderScan as __renderScan } from '../ui/modal.js';
import { lwDispatch, lwOnce } from '../core/livewire.js';
import { OFFLINE_MODE_ENABLED, DEVICE_UUID, isAuthenticated } from '../core/config.js';
import { withDb } from '../data/idb.js';
import { Sync } from '../sync/index.js';

export function parseUuid(raw) {
    const m = /([0-9a-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})/i.exec(raw || '');
    return m ? m[1] : null;
};

export async function handleScanData(raw, type) {

    const renderScan = (payload = {}) => {

        if (!window.SIMULATOR_MODE) return __renderScan(payload);

        const { status = 'info', message = '', badge, allowProceed, type: pType } = payload || {};
        const uuid = badge?.uuid ?? badge?.id ?? null;
        window.simulatorLog?.({ status, message, uuid, type: pType });

        // Auto proceed if possible
        const canProceed = allowProceed !== false && uuid && (badge?.is_allowed ?? true);

        if (canProceed) {
            try {
                proceed(uuid, pType);
            } catch (e) {
                window.simulatorLog?.({
                    status: 'danger',
                    message: `Proceed failed: ${e?.message || e}`,
                    uuid,
                    type: pType
                });
            }
        }

    };

    if (App.state != State.READY) {
        renderScan({
            status: 'danger',
            message: 'System not ready yet.',
            allowProceed: false,
            type
        });
        return;
    };
    if (!isAuthenticated()) {
        redirectToAuth();
        return;
    };

    const m = /([0-9a-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})/i.exec(raw || '');
    if (!m) {
        renderScan({
            status: 'danger',
            message: 'Wrong QR code format',
            allowProceed: false,
            badge: {
                uuid: raw
            },
            type
        });
        return;
    }

    const uuid = m[1];

    if (navigator.onLine) {
        await App.tryBackgroundSync(true);
    } else if (!OFFLINE_MODE_ENABLED) {
        renderScan({
            status: 'danger',
            message: 'Something went wrong',
            details: 'Offline mode disabled. Connect to the internet to continue.',
            allowProceed: false,
            type
        });
        return;
    }

    const badge = await getBadge(uuid);

    if (badge === UNAVAILABLE) return; // message already shown

    if (!badge) {
        renderScan({
            status: 'danger',
            message: 'Forbidden',
            details: 'No badge found.',
            allowProceed: false,
            type
        });
        return;
    }

    if (!badge.is_allowed) {
        renderScan({
            status: 'danger',
            message: 'Forbidden',
            details: badge.access_details || 'Badge not allowed.',
            allowProceed: false,
            badge,
            type
        });
        return;
    };

    renderScan({
        badge,
        status: 'warning',
        message: 'Allowed',
        type
    });
};

export async function proceed(uuid, type) {
    let badge = null;
    const offline = !navigator.onLine;

    // If we're offline but offline mode is disabled: fail fast.
    if (offline && !OFFLINE_MODE_ENABLED) {
        // Note: wrapper gère l'affichage/no-modal côté simulateur
        const render = (payload) => window.SIMULATOR_MODE ? window.simulatorLog?.(payload) : __renderScan(payload);
        render({
            status: 'danger',
            message: 'Unknown badge',
            allowProceed: false,
            type,
            badge: { uuid }
        });
        return;
    }

    // When offline mode is enabled, consult local DB first (works both online/offline).
    if (OFFLINE_MODE_ENABLED) {
        badge = await withDb(db => db.get('badges', uuid));
    }

    // Offline with no local badge ⇒ "Unknown badge".
    if (offline && !badge) {
        renderScan({ status: 'danger', message: 'Unknown badge', allowProceed: false });
        return;
    }

    UI.setInProgress();

    // Online path: if we didn't find a local badge, ask the server.
    if (!offline && !badge) {
        const p = lwOnce('checkin.badge.server.proceeded');
        lwDispatch('checkin.badge.server.proceed', {
            badge_uuid: uuid,
            details: { device_uuid: DEVICE_UUID, validated_from: type || 'UNKNOWN' }
        });
        // assign into the outer `badge` (avoid shadowing)
        ({ badge } = await p);
    }

    // Build a local log entry (used for pushOne)
    const logs = Array.isArray(badge?.local_scan_logs) ? badge.local_scan_logs.slice() : [];
    logs.push({
        scan_success: true,
        created_at_ts: Date.now(),
        local_scan: true,
        validated_from: type || 'UNKNOWN'
    });

    // If we have a local badge and offline mode is enabled, persist + try to sync.
    if (badge && OFFLINE_MODE_ENABLED) {
        const current = Number.isFinite(badge.scan_count) ? badge.scan_count : 0;
        const next = {
            ...badge,
            local_scan_logs: logs,
            scan_count: current + 1,
            synced: false
        };
        await withDb(db => db.put('badges', next));
        await Sync.pushOne(next);
    }

    lwDispatch('checkin.scan.proceeded');

    if (window.SIMULATOR_MODE) window.simulatorLog?.({ status: 'success', message: 'Proceed OK', uuid, type });

};

export const dispatchScan = async ({ data, type }) => {
    document.body.classList.add('fetching');
    try {
        await handleScanData(data, type);
    } finally {
        document.body.classList.remove('fetching');
        // Lazy import to avoid circular dependency with features/search.js
        const { SEARCH } = await import('../features/search.js');
        if (SEARCH?.reset) SEARCH.reset();
    }
};

export const cancel = async (uuid) => {
    lwDispatch('checkin.scan.cancel');

    if (window.SIMULATOR_MODE) window.simulatorLog?.({ status: 'warning', message: 'Scan cancelled', uuid, type: 'SIMULATOR' });
};
